import React, { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { default as AddItemImage } from "../../assets/add-Item.jpg";
import FormMolecule from "../Form/FormMolecule";
import { useForm, useWatch } from "react-hook-form";
import {
  ADD_RETURN_ITEMS_FIELDS,
  PRODUCT_DETAILS,
  resetFormValues,
} from "./ReturnItemFields";
import {GrayActionButton} from "./ReturnItemFormStyle";
import { WarningIcon } from "../../atoms/WarningIcon/WarningIcon";
import { Parser } from "../../utilities/DataMatrixParser/Parser";
import { fields } from "../../utilities/DataMatrixParser/constants";
import { CallToActionButton } from "../../atoms/callToActionButton/CallToActionButton";
import { useAddRequestItem } from "../../hooks/useAddRequestItem";
import { useUpdateRequestItem } from "../../hooks/useUpdateRequestItem";
import Loader from "../../atoms/Loader/LoaderAtom";
import { ReturnRequestServiceType, UserRole } from "../../utilities/Enums";
import { useDebouncedDrugLookup } from "../../hooks/useDebouncedDrugLookup";
import { toastInfoDelayedClose } from "../../utilities/toast";
import { extractNDC } from "../../utilities/DataMatrixParser/extractNDC";
import { formatDate } from "../../utilities/DataMatrixParser/formatDate";
import { formatNdc, clampNdcInputElevenDigits, applyNdcInputChange, isGs1StreamInProgress, isNdcReadyForDrugLookup, isNdcValid, shouldArmBarcodeScanTimer } from "../../utilities/ndc/ndcUtils";
import {
  logNdcConstructed,
  markNdcConstructStart,
  resetNdcConstructTiming,
} from "../../utilities/ndc/ndcScanTiming";
import { getPackageSizeUom, getPackageSizeNumeric } from "../../utilities/getPackageSizeUom";
import { useLoaderDispatch, useLoaderState } from "../../context/LoaderContext";
import { ItemsTable } from "../../network/models/TableInterfaces";
import { useHistory } from "react-router-dom";
import { isRoleIncluded } from "../../utilities/checkUserRole";
import { useAuthState } from "../../context/AuthContext";
import {
  Product,
  CardsWrapper,
  ImageWrapper,
  ItemEntry,
  useStyles,
  ProductSearchAndImageWrapper,
  ProductSearchWrapper,
} from "./ReturnItemFormStyle";
import { ReturnItemFields } from "../../network/models/FormInterfaces";
import { useCreateReturnRequest } from "../../hooks/useCreateReturnRequest";
import SearchProductModal, { SearchType } from "./SearchProductModal";
import { useManufacturers } from "../../hooks/useManufacturers";

interface ReturnItemFormProps {
  pharmacyId: string;
  requestId: string;
  onAdd: Function;
  onUpdate: Function;
  editingItem?: ItemsTable;
  formRef?: RefObject<HTMLDivElement>;
  locationState?: {
    serviceType: ReturnRequestServiceType;
  };
}

/** Idle delay after last barcode key before trim + drug lookup (barcode only; QR uses Enter). */
const SCAN_COMPLETE_DEBOUNCE_MS = 200;

export const ReturnItemForm: React.FC<ReturnItemFormProps> = (props) => {
  const [ndcInput, setNdcInput] = useState<string>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [modalType, setModalType] = useState<SearchType>(
    SearchType.Manufacturer
  );

  const parser = useRef(new Parser(fields));
  const scannerInput = useRef<string[]>([]);
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ndcRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);

  const itemForm = useForm<ReturnItemFields>({
    values: resetFormValues(props.editingItem),
  });

  const {
    productData,
    scheduleDrugLookup,
    resetDrugLookupState,
    cancelDrugLookupDebounce,
    rawNdcRef,
    isUpdatingNdcRef,
  } = useDebouncedDrugLookup({ itemForm, quantityRef });
  const ndc: string = useWatch({ name: "ndc", control: itemForm.control });
  const quantity = useWatch({ name: "quantity", control: itemForm.control });
  const quantityType = useWatch({
    name: "quantityType",
    control: itemForm.control,
  });
  const pkgSize = useWatch({ name: "pkgSize", control: itemForm.control });
  const csc: string = useWatch({
    name: "controlledSubstanceCode",
    control: itemForm.control,
  });
  const WACPrice = useWatch({ name: "WACPrice", control: itemForm.control });
  const estimatedReturnValue = useWatch({ name: "estimatedReturnValue", control: itemForm.control });

  const partialQtyUom =
    quantityType === "partial"
      ? getPackageSizeUom(
          productData?.package_size ?? pkgSize ?? props.editingItem?.packageSize
        )
      : "";

  const history = useHistory();
  const loaderDispatch = useLoaderDispatch();
  const { loading } = useLoaderState();
  const { user } = useAuthState();
  const classes = useStyles(props);
  const [processScan, setProcessScan] = useState<string>("");
  const [newItem, setNewItem] = useAddRequestItem(props.pharmacyId);
  const [updatedItem, setUpdatedItem] = useUpdateRequestItem(
    props.pharmacyId,
    props.requestId
  );
  const [manufacturers, setManufacturers, manufacturerState] = useManufacturers('');
  // expose fetchNextPage to modal via global to avoid prop drilling in this quick fix
  (window as any).manufacturersFetchNextPage = (manufacturerState as any).fetchNextPage;
  const [newReturnRequest, setNewReturnRequest] = useCreateReturnRequest(
    props.pharmacyId
  );

  const clearScanTimer = useCallback(() => {
    if (scanTimerRef.current) {
      clearTimeout(scanTimerRef.current);
      scanTimerRef.current = null;
    }
  }, []);

  const completeBarcodeScan = useCallback(
    (scannedValue: string, source: "barcode-timer" | "barcode-enter" | "barcode-wedge") => {
      const value = scannedValue.trim();
      if (!value) return;

      const normalized = formatNdc(value);
      if (!isNdcReadyForDrugLookup(normalized)) return;

      if (normalized !== rawNdcRef.current) {
      itemForm.setValue("ndc", normalized, {
        shouldValidate: true,
        shouldDirty: true,
      });
      }
      logNdcConstructed(normalized, source);
      scheduleDrugLookup(normalized, true);
    },
    [itemForm, rawNdcRef, scheduleDrugLookup]
  );

  const armScanTimer = useCallback(
  (projectedValue?: string) => {
    clearScanTimer();
    const current =
      projectedValue ?? ndcRef.current?.value ?? itemForm.getValues("ndc") ?? "";
    if (!shouldArmBarcodeScanTimer(current)) return;

    scanTimerRef.current = setTimeout(() => {
      scanTimerRef.current = null;
      const value = (ndcRef.current?.value ?? itemForm.getValues("ndc") ?? "").trim();
      completeBarcodeScan(value, "barcode-timer");
    }, SCAN_COMPLETE_DEBOUNCE_MS);
  }, [clearScanTimer, completeBarcodeScan, itemForm]);

  useEffect(() => () => clearScanTimer(), [clearScanTimer]);

  useEffect(() => {
    if (productData) {
      clearScanTimer();
    }
  }, [productData, clearScanTimer]);

  useEffect(() => {
    if (!processScan) return;
    const extractedData = parser.current.parseRawGS1(processScan);
    const { GTIN, LOT, EXP, SN } = extractedData;
    if (GTIN) {
      const ndcObj = extractNDC(GTIN); 
      if (ndcObj?.data) {
        itemForm.setValue("ndc", formatNdc(ndcObj.data), { shouldValidate: true, shouldDirty: true });
        logNdcConstructed(ndcObj.data, "qr-gs1");
        scheduleDrugLookup(ndcObj.data, true);
      }
      //itemForm.setValue("gtin14", GTIN, { shouldValidate: true, shouldDirty: true });
    }
    if (LOT) {
      itemForm.setValue("lot", LOT, { shouldValidate: true, shouldDirty: true });
    }
    if (SN) {
      //itemForm.setValue("serialNumber", SN, { shouldValidate: true, shouldDirty: true });
    }
    if (EXP) {
      const formatted = formatDate(EXP);
      itemForm.setValue("expDate", formatted ?? "", { shouldValidate: true, shouldDirty: true });
    }
    toastInfoDelayedClose(processScan);
    setProcessScan("");
    scannerInput.current = []; 
  }, [processScan, itemForm, scheduleDrugLookup]);

  useEffect(() => {
    if (isUpdatingNdcRef.current) return;
    if (!ndc) {
      clearScanTimer();
      resetNdcConstructTiming();
      resetDrugLookupState();
      parser.current.resetParser();
      scannerInput.current = [];
      itemForm.reset();
      return;
    }

    const dashlessLength = ndc.replace(/-/g, "").length;
    if (dashlessLength > 11) {
      if (isGs1StreamInProgress(ndc)) {
        return;
      }
      const normalized = formatNdc(ndc);
      if (isNdcReadyForDrugLookup(normalized)) {
        clearScanTimer();
        completeBarcodeScan(ndc, "barcode-wedge");
        return;
      }
      clearScanTimer();
      cancelDrugLookupDebounce();
      return;
    }

    if (isNdcReadyForDrugLookup(ndc)) {
      scheduleDrugLookup(ndc);
      return;
    }

    if (isNdcValid(ndc)) {
      return;
    }

    if (parser.current.shouldParserExit() && ndc.length > 30) {
      const ndcStream = ndc.split("");

      scannerInput.current.reverse();
      ndcStream.reverse();
      const mergedNdc: string[] = [];

      // Merge scanner input and NDC field as each one contains needed data for the parser
      let idxScanner = 0;
      let idxStream = 0;
      while (idxStream < ndcStream.length) {
        const key =
          idxScanner < scannerInput.current.length
            ? scannerInput.current[idxScanner]
            : "";
        if (isNaN(+key)) {
          mergedNdc.push(key);
          idxScanner++;
          if (key === ndcStream[idxStream]) idxStream++;
        } else {
          mergedNdc.push(ndcStream[idxStream]);
          idxStream++;
          if (idxScanner < scannerInput.current.length) idxScanner++;
        }
      }
      mergedNdc.reverse();

      const message = mergedNdc.join("-");
      toastInfoDelayedClose(message ?? "");
      itemForm.reset();
      mergedNdc.forEach((char) => parser.current.parseChar(char));
      const results = parser.current.getFields();
      results.forEach(({ fieldName, data }) => {
        if (fieldName === "LOT") {
          itemForm.setValue("lot", data);
        } else if (fieldName === "GTIN") {
          const ndcExtracted = extractNDC(data);
          // setValue("gtin14", data);
          const extractedNdc = ndcExtracted?.data ?? "";
          itemForm.setValue("ndc", formatNdc(extractedNdc));
          if (extractedNdc) {
            logNdcConstructed(extractedNdc, "qr-stream-parser");
            scheduleDrugLookup(extractedNdc, true);
          }
        } else if (fieldName === "EXP") {
          const formattedDate = formatDate(data);
          itemForm.setValue("expDate", formattedDate ?? "");
        } else if (fieldName === "SN") {
          // setValue("serialNumber", data)
        }
      });
      scannerInput.current = [];
    }
  }, [ndc, itemForm, scheduleDrugLookup, resetDrugLookupState, cancelDrugLookupDebounce, clearScanTimer, isUpdatingNdcRef, completeBarcodeScan]);

  useEffect(() => {
    if (!quantity) {
      itemForm.setValue("WACPrice", undefined);
      itemForm.setValue("estimatedReturnValue", undefined);
      return;
    }
    if (!quantityType || !productData) return;
    const qty = Number(quantity) || 0;
    const pkgSize = getPackageSizeNumeric(productData.package_size);
    const wacPkg = Number(productData.Package_Price) || 0;
    const wacUnit = Number(productData.Unit_Price) || 0;
    const actualPkg = Number(productData.Package_Actual_Price) || 0;
    const actualUnit = Number(
      productData.Unit_Actual_Price ?? 0
    ) || 0;

    const wacTotal =
      quantityType !== "partial" ? qty * wacPkg : qty * wacUnit;

    let est: number | undefined;

    if (quantityType !== "partial") {
      est = actualPkg > 0 ? qty * actualPkg : qty * wacPkg * 0.7;
    } else {
      const derivedActualUnit =
        actualPkg > 0 && pkgSize > 0 ? actualPkg / pkgSize : 0;

      const unitActualToUse =
        actualUnit > 0 ? actualUnit :
          derivedActualUnit > 0 ? derivedActualUnit : 0;

      est = unitActualToUse > 0 ? qty * unitActualToUse : qty * wacUnit * 0.7;
    }

    itemForm.setValue("WACPrice", wacTotal !== undefined ? Math.round(wacTotal * 100) / 100 : undefined, { shouldValidate: true });
    itemForm.setValue("estimatedReturnValue", est !== undefined ? Math.round(est * 100) / 100 : undefined, { shouldValidate: true });

  }, [quantity, quantityType, productData, itemForm]);

  useEffect(() => {
    const qty = itemForm.getValues("quantity");
    const hasQuantity =
      qty !== undefined && qty !== null && String(qty).trim() !== "";

    if (hasQuantity) {
      void itemForm.trigger("quantity");
    } else {
      itemForm.clearErrors("quantity");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityType]);


  const onSubmit = itemForm.handleSubmit(async (data) => {
    const submitData = {
      ...data,
      ndc: rawNdcRef.current || data.ndc.replace(/-/g, ""),
    };

    if (props.editingItem) {
      setUpdatedItem(props.editingItem.id.toString(), submitData, {
        onSuccess: () => {
          props.onAdd();
          props.onUpdate();
          parser.current.resetParser();
        },
      });
    } else if (props.requestId) {
      setNewItem(props.requestId, submitData, {
        onSuccess: () => {
          props.onAdd();
          parser.current.resetParser();
        },
      });
    } else {
      setNewReturnRequest(props.locationState, {
        onSuccess: (returnRequest) =>
          returnRequest &&
          setNewItem(returnRequest.data.id, submitData, {
            onSuccess: () => {
              itemForm.reset();
              parser.current.resetParser();

              const roleIncluded = isRoleIncluded(
                [UserRole.Admin, UserRole.Warehouse, UserRole.FieldProcessor],
                user?.role
              );

              history.push(
                `${roleIncluded ? "/admin" : ""}/pharmacies/${props.pharmacyId
                }/returnrequests/${returnRequest.data.id}/add`
              );
            },
          }),
      });
    }
    itemForm.reset();
    ndcRef.current?.focus();
  });

  const handleSelect = (item: { drugName?: string; ndc?: string } | string) => {
    if (typeof item === "string" || !item.ndc) {
      setSelectedManufacturer(
        typeof item === "string" ? item : item.drugName || ""
      );
      setModalType(SearchType.Product);
    } else if (item.ndc) {
      setSelectedProduct(item.drugName || "");
      itemForm.setValue("ndc", clampNdcInputElevenDigits(item.ndc));
      setIsModalOpen(false);
    }
  };

  const shouldShowHighValueWarning = isRoleIncluded(
  [UserRole.Warehouse, UserRole.FieldProcessor], 
  user?.role
);

  return (
    <form onSubmit={onSubmit}>
      <div>
        {isModalOpen && (
          <SearchProductModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSelect={handleSelect}
            searchType={modalType}
            selectedManufacturer={selectedManufacturer}
            onManufacturerSelect={setSelectedManufacturer}
            manufacturers={manufacturers?.map((name: string) => ({ drugName: name })) ?? []}
            loadingManufacturers={manufacturerState.isFetching}
            loaderDispatch={loaderDispatch}
            setValue={itemForm.setValue}
            onManufacturerSearchChange={setManufacturers}
          />
        )}
      </div>
      <CardsWrapper ref={props.formRef}>
        <ProductSearchAndImageWrapper>
          <ProductSearchWrapper>
            <h1 className="title">Product Search</h1>
            <div className="button-group">
              <button
                className="search-button"
                type="button"
                onClick={() => {
                  setModalType(SearchType.Manufacturer);
                  setIsModalOpen(true)
                }}
              >
                {selectedManufacturer
                  ? selectedManufacturer
                  : "Select Manufacturer"}
              </button>

              <button
                className="search-button"
                type="button"
                onClick={() => {
                  setModalType(SearchType.Product);
                  setIsModalOpen(true)
                }}
              >
                {selectedProduct ? selectedProduct : "Select Product"}
              </button>
            </div>
          </ProductSearchWrapper>
          <ImageWrapper style={{ paddingRight: "0.8rem" }}>
            <img src={AddItemImage} alt="login" />
          </ImageWrapper>
        </ProductSearchAndImageWrapper>
        <ItemEntry
          onWheelCapture={(e) => {
            const t = e.target as HTMLInputElement;
            if (t?.name === "quantity") t.blur();
          }}
        >
          <FormMolecule
            label="Item Entry"
            control={itemForm.control as any}
            list={ADD_RETURN_ITEMS_FIELDS.map((row) =>
            row.map((field) => {
            let fieldConfig: any = {
              ...field,
              disabled: field.controllingName === "ndc" ? !!props.editingItem : field.disabled,
            };

            if (field.controllingName === "ndc") {
              const recognizedDigits = productData?.product_ndc
                ? formatNdc(productData.product_ndc)
                : "";
              fieldConfig = {
                ...fieldConfig,
                onFocus: () => clearScanTimer(),
                formatValue: (next: string, previous = "") =>
                  applyNdcInputChange(previous, next, {
                    lockDashes:
                      !!recognizedDigits &&
                      formatNdc(previous) === recognizedDigits,
                  }),
              };
            }

            if (field.controllingName === "estimatedReturnValue" && Number(estimatedReturnValue) > 10000 && shouldShowHighValueWarning) {
                fieldConfig = {
                  ...fieldConfig,
                  hintText: <span >⚠️Warning: Estimated return value exceeds 10,000</span> as any
                };
              }
            

          if (field.controllingName === "WACPrice" && Number(WACPrice) > 10000 && shouldShowHighValueWarning) {
                fieldConfig = {
                  ...fieldConfig,
                  hintText: <span >⚠️Warning: WAC Price exceeds 10,000</span> as any
                };
              }

            if (field.controllingName === "quantity" && partialQtyUom) {
              fieldConfig = {
                ...fieldConfig,
                endAdornment: partialQtyUom,
                };
              }
            

            return fieldConfig;
          })
        )}
            cautionIcon={csc && !isNaN(+csc) ? <WarningIcon controlSubstance={+csc} /> : null}
            onInputFieldKeyDown={(
              e: React.KeyboardEvent<HTMLDivElement>,
              controlingName: string
            ) => {
              if (controlingName === "ndc") {
                if (e.ctrlKey || e.metaKey || e.altKey) {
                  return;
                }

                if (e.key === "Enter") {
                  e.preventDefault();
                  parser.current.quickScan(e.key);
                  clearScanTimer();

                  const value = (ndcRef.current?.value ?? itemForm.getValues("ndc") ?? "").trim();
                if (isGs1StreamInProgress(value)) {
                  if (value.length > 15) {
                    setProcessScan(value);
                  }
                  } else {
                    completeBarcodeScan(value, "barcode-enter");
                  }
                  setTimeout(() => {
                    quantityRef.current?.focus();
                  }, 50);
                  
                  return;
                }
                if (parser.current.shouldParserExit()) return;
                if (e.key.length === 1 && /^\d$/.test(e.key)) {
                  markNdcConstructStart();
                scannerInput.current.push(e.key);
                parser.current.quickScan(e.key);
                const prior = ndcRef.current?.value ?? itemForm.getValues("ndc") ?? "";
                const projected = `${prior}${e.key}`;
                if (!isGs1StreamInProgress(projected)) {
                  armScanTimer(projected);
                  }
                }
              }
                  
              if (controlingName === "quantity") {
                if (e.key === "Enter") {
                  return; 
                }

                const isDigit = /^\d$/.test(e.key);
                const isDecimalPoint = e.key === ".";
                const isPrintable = e.key.length === 1;
                const allowDecimal =
                  itemForm.getValues("quantityType") === "partial";

                if (isPrintable && !isDigit) {
                  if (allowDecimal && isDecimalPoint) {
                    const current =
                      (e.target as HTMLInputElement).value ?? "";
                    if (current.includes(".")) {
                      e.preventDefault();
                    }
                    return;
                  }
                  e.preventDefault();
                  return;
                }
              }
            }}
            ndcRef={ndcRef}
            quantityRef={quantityRef}
          />
        </ItemEntry>
        {!loading ? (
          <Product>
            <FormMolecule
              label="Product details"
              control={itemForm.control as any}
              list={PRODUCT_DETAILS}
            />
          </Product>
        ) : (
          <Loader />
        )}
      </CardsWrapper>
      <div className={classes.buttonWrapper}>
        <CallToActionButton
          type="submit"
          height={"70px"}
          text={props.editingItem ? "Update Item" : "Add Item"}
          width={"20%"}
        />

        <div style={{ width: "1%" }} />

        <GrayActionButton
          type="button"
          onClick={() => {
          clearScanTimer();
          resetNdcConstructTiming();
          resetDrugLookupState();
          itemForm.reset();
          parser.current.resetParser();
          scannerInput.current = [];
          setProcessScan("");
          setTimeout(() => {
          ndcRef.current?.focus();
          }, 50);
        }}
        disabled={!!props.editingItem}
        >
          Clear
        </GrayActionButton>

      </div>
    </form>
  );
};
